import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { batchUploadImagesToOss } from '../../api/agiBackUpload';
import { getUserAvatarAssetGroup } from '../../api/userAvatarAssetGroup';
import { createVirtualPortrait } from '../../api/virtualPortrait';
import { useSeedanceImageGenStore } from '../../store/seedanceImageGenStore';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import { VIRTUAL_PORTRAIT_PROJECT, VP_ASSET_TYPE_IMAGE } from '../../utils/seedanceConstants';
import { extractApiMessage } from '../../utils/seedanceRequestUtils';
import {
  buildVirtualAvatarGroupOptions,
  resolveVirtualAvatarGroupSelection,
  type VirtualAvatarGroupOption,
} from '../../utils/avatarAssetGroup';
import {
  getLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
  setLocalStorageValue,
} from '../../utils/seedanceStorage';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ImageLightbox } from '../../components/ImageLightbox';
import { PromptTextHighlight } from '../../components/PromptTextHighlight';
import {
  bindPromptHighlightOverlaySync,
  promptHighlightOverlayHandlers,
  syncPromptHighlightScroll,
} from '../../utils/promptHighlightOverlay';
import type { PromptMentionCandidate } from '../../store/seedanceTypes';
import {
  ASPECT_OPTIONS,
  isOpenAiGptImageModel,
  supportsImageGenGroupMode,
  supportsOpenAiGptImageEdit,
  type AspectChoice,
  type GenRecord,
  type GenRecordReferenceInput,
  type ResolutionChoice,
  referenceInputsToRunPayload,
  RESOLUTION_OPTIONS,
} from './imageGenRecords';
import {
  adjustPromptMentionsAfterSlotRemoved,
  buildPromptMentionLabel,
  findPromptMentionCandidate,
  getPromptMentionAtIndex,
  getTextareaIndexNearPoint,
  promptTextHasMediaRefSegments,
} from '../../utils/seedancePromptUtils';
import {
  computeImageGenFeedInitialPageSize,
  computeImageGenFeedLoadMorePageSize,
  IMAGE_GEN_FEED_PAGE_SIZE_MIN,
} from '../../utils/imageGenFeedPaging';
import './imageGen.css';

const ImageGenMentionThumbPopover: React.FC<{
  hover: { thumbUrl: string | null; label: string; x: number; y: number } | null;
}> = ({ hover }) => {
  if (!hover) return null;
  const popW = 180;
  const popH = 210;
  const pad = 14;
  let left = hover.x + pad;
  let top = hover.y + pad;
  if (typeof window !== 'undefined') {
    left = Math.min(left, window.innerWidth - popW - 10);
    top = Math.min(top, window.innerHeight - popH - 10);
    left = Math.max(10, left);
    top = Math.max(10, top);
  }
  return (
    <div
      className="ig-imagegen-mention-thumb-pop"
      style={{ left, top }}
      role="tooltip"
      aria-hidden
    >
      {hover.thumbUrl ? (
        <img src={hover.thumbUrl} alt="" draggable={false} />
      ) : (
        <div className="ig-imagegen-mention-thumb-pop-empty">暂无预览图</div>
      )}
      <div className="ig-imagegen-mention-thumb-pop-cap">{hover.label}</div>
    </div>
  );
};

/** 文生图模型（Gemini 原生 generateContent + OpenAI Images） */
const IMAGE_GEN_MODEL_OPTIONS = [
  {
    value: 'gemini-2.5-flash-image',
    label: 'Gemini 2.5 Flash Image',
    caption: '2.5 Flash',
    recommended: true,
  },
  { value: 'gemini-3-pro-image-preview', label: 'Gemini 3 Pro Image', caption: '3 Pro' },
  {
    value: 'gemini-3.1-flash-image-preview',
    label: 'Gemini 3.1 Flash Image',
    caption: '3.1 Flash',
  },
  { value: 'gpt-image-1', label: 'GPT Image 1', caption: 'GPT-Img1' },
  { value: 'gpt-image-2', label: 'GPT Image 2', caption: 'GPT-Img2' },
] as const;

/** OpenAI Images API，`POST /v1/images/generations` */
const OPENAI_GPT_IMAGE_MODELS = ['gpt-image-1', 'gpt-image-2'] as const;

const GPT_IMAGE_1_MODEL = 'gpt-image-1';

/** GPT Image 2 /images/edits 单次参考图上限 */
const GPT_IMAGE_2_MAX_REFERENCE_IMAGES = 8;

/** 底栏每张参考图的本地预览与 payload */
interface UiImageRef {
  id: string;
  thumb: string;
  base64: string;
  mime: string;
}

function randomRefUiId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `r_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** 从持久化记录恢复底栏参考图 */
function genRecordReferenceInputsToUiRefs(
  inputs: GenRecordReferenceInput[] | undefined,
): UiImageRef[] {
  if (!inputs?.length) return [];
  return inputs.map((r) => {
    const mime = r.mime.trim();
    const base64 = r.base64.replace(/\s/g, '');
    return {
      id: randomRefUiId(),
      thumb: `data:${mime};base64,${base64}`,
      base64,
      mime,
    };
  });
}

/** 记录在案的模型 Id 是否与顶栏可选一致（避免脏数据写入 setModel） */
function isRecordedImageGenModel(m: string): boolean {
  return IMAGE_GEN_MODEL_OPTIONS.some((o) => o.value === m.trim());
}

const resolutionSet = new Set<string>(RESOLUTION_OPTIONS);
const aspectSet = new Set<string>(ASPECT_OPTIONS);

/** 从生成记录还原组图 / 单张（兼容旧记录：无字段时按成片数量推断） */
function resolveRecordGenMode(rec: GenRecord): {
  genMode: 'single' | 'group';
  groupCount: number;
} {
  if (rec.genMode === 'group' && supportsImageGenGroupMode(rec.model)) {
    const gc = rec.groupCount ?? rec.images.length;
    if (gc >= 2) {
      return { genMode: 'group', groupCount: Math.min(4, Math.max(2, gc)) };
    }
  }
  if (rec.genMode === 'single') {
    return { genMode: 'single', groupCount: rec.groupCount ?? 4 };
  }
  const n = rec.images.length;
  if (supportsImageGenGroupMode(rec.model) && n >= 2) {
    return { genMode: 'group', groupCount: Math.min(4, Math.max(2, n)) };
  }
  return { genMode: 'single', groupCount: 4 };
}

function resolveOpenAiImageSizeFromRecord(rec: GenRecord): OpenAiGptImageSizeChoice {
  const sz = rec.imageSize?.trim();
  if (sz && isValidOpenAiImageSizeForModel(rec.model, sz)) {
    return sz as OpenAiGptImageSizeChoice;
  }
  return 'auto';
}

/** 将记录中的模型与画面参数写回顶栏 / 底栏（重新编辑） */
function applyGenRecordModelConfig(
  rec: GenRecord,
  setters: {
    setModel: (m: string) => void;
    setResolutionUi: (r: ResolutionChoice) => void;
    setAspectUi: (a: AspectChoice) => void;
    setOpenAiImageSize: (s: OpenAiGptImageSizeChoice) => void;
    setGenMode: (m: 'single' | 'group') => void;
    setGroupCount: (n: number) => void;
  },
): void {
  if (isRecordedImageGenModel(rec.model)) {
    setters.setModel(rec.model.trim());
  }
  if (resolutionSet.has(rec.resolution)) {
    setters.setResolutionUi(rec.resolution);
  }
  if (aspectSet.has(rec.aspect)) {
    setters.setAspectUi(rec.aspect);
  }
  if (isOpenAiGptImageModel(rec.model)) {
    setters.setOpenAiImageSize(resolveOpenAiImageSizeFromRecord(rec));
  }
  const { genMode: gm, groupCount: gc } = resolveRecordGenMode(rec);
  setters.setGenMode(gm);
  setters.setGroupCount(gc);
}

function readFileAsUiImageRef(file: File): Promise<UiImageRef | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const comma = dataUrl.indexOf(',');
      const semi = dataUrl.indexOf(';');
      const mime = semi > 5 ? dataUrl.slice(5, semi) : 'image/png';
      const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
      resolve(
        base64
          ? {
              id: randomRefUiId(),
              thumb: dataUrl,
              base64,
              mime,
            }
          : null,
      );
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}
/** 文档：gpt-image-1 仅支持 1024x1024、1536x1024、1024x1536、auto */
const OPENAI_GPT_IMAGE_1_SIZE_OPTIONS = ['1024x1024', '1536x1024', '1024x1536', 'auto'] as const;

/** gpt-image-2：扩展尺寸 + 与 1 共有的几款 */
const OPENAI_GPT_IMAGE_2_SIZE_OPTIONS = [
  '1536x864',
  '2560x1440',
  '3840x2160',
  '1024x1024',
  '1536x1024',
  '1024x1536',
  'auto',
] as const;

type OpenAiGptImageSizeChoice =
  | (typeof OPENAI_GPT_IMAGE_1_SIZE_OPTIONS)[number]
  | (typeof OPENAI_GPT_IMAGE_2_SIZE_OPTIONS)[number];

const allStoredOpenAiImageSizes = new Set<string>([
  ...OPENAI_GPT_IMAGE_1_SIZE_OPTIONS,
  ...OPENAI_GPT_IMAGE_2_SIZE_OPTIONS,
]);

function isGptImage1Model(modelId: string): boolean {
  return modelId.trim() === GPT_IMAGE_1_MODEL;
}

function openAiImageSizeOptionsForModel(modelId: string): readonly string[] {
  return isGptImage1Model(modelId) ? OPENAI_GPT_IMAGE_1_SIZE_OPTIONS : OPENAI_GPT_IMAGE_2_SIZE_OPTIONS;
}

function isValidOpenAiImageSizeForModel(modelId: string, size: string): boolean {
  return openAiImageSizeOptionsForModel(modelId).includes(size);
}

/** 下拉展示：image-1 带文档说明 */
function formatOpenAiImageSizeOptionLabel(modelId: string, size: string): string {
  if (isGptImage1Model(modelId)) {
    if (size === '1536x1024') return '1536x1024（横向）';
    if (size === '1024x1536') return '1024x1536（纵向）';
    if (size === 'auto') return '自动（默认）';
  }
  return size;
}

/** 生成记录留存说明（空状态、顶栏提示、底栏共用） */
const IMAGE_GEN_STORAGE_HINT =
  '生成记录仅保留最近 14 天，记录保存在本机浏览器，清理缓存、重装系统或更换设备后可能无法找回，加入素材库或下载到本地可长期留存。';
const IMAGE_GEN_DOCK_DISCLAIMER =
  '试用体验内容均由人工智能模型生成，不代表平台立场。';

const formatTime = (ms: number) =>
  new Date(ms).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const guessExtFromMime = (mime: string) => {
  const m = mime.toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('webp')) return 'webp';
  if (m.includes('gif')) return 'gif';
  return 'jpg';
};

/** 把图片源（http(s) URL 或 data:image/...;base64,xxx）转成可上传到 OSS 的 File。 */
const fetchImageAsFile = async (src: string, fallbackName: string): Promise<File> => {
  if (src.startsWith('data:')) {
    const match = /^data:([^;,]+);base64,(.*)$/i.exec(src);
    if (!match) throw new Error('无效的 data URL');
    const mime = match[1] || 'image/png';
    const bin = atob(match[2]);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new File([arr], `${fallbackName}.${guessExtFromMime(mime)}`, { type: mime });
  }
  const res = await fetch(src);
  if (!res.ok) throw new Error(`下载图片失败：HTTP ${res.status}`);
  const blob = await res.blob();
  const ext = guessExtFromMime(blob.type || 'image/jpeg');
  return new File([blob], `${fallbackName}.${ext}`, { type: blob.type || 'image/jpeg' });
};

const triggerDownload = (url: string, name: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/** 加入素材库弹窗默认名称（取提示词，便于用户再编辑） */
function defaultLibAssetNameFromRec(rec: GenRecord): string {
  const p = rec.prompt.trim();
  if (!p) return `生成_${rec.id.slice(0, 8)}`;
  return p.length > 120 ? p.slice(0, 120) : p;
}

/** data/blob 上传 OSS 时的文件名主干（去掉路径不安全字符） */
function stemForLibraryUploadFile(name: string): string {
  const t = name
    .trim()
    .slice(0, 80)
    .replace(/[/\\?%*:|"<>]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^\.+|\.+$/g, '')
    .replace(/^_|_$/g, '');
  return t || 'image_gen_asset';
}

const ImageGenPage: React.FC = () => {
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const addDebug = useSeedanceUiStore((s) => s.addDebug);

  const [model, setModel] = useState<string>(IMAGE_GEN_MODEL_OPTIONS[0].value);
  const [prompt, setPrompt] = useState('');
  const [aspectUi, setAspectUi] = useState<AspectChoice>('16:9');
  const [resolutionUi, setResolutionUi] = useState<ResolutionChoice>('2K');
  const [openAiImageSize, setOpenAiImageSize] = useState<OpenAiGptImageSizeChoice>('auto');
  const [paramsOpen, setParamsOpen] = useState(false);
  const [dockExpanded, setDockExpanded] = useState(false);
  /** 单张：始终 1 张；组图：并行 2～4 张 */
  const [genMode, setGenMode] = useState<'single' | 'group'>('single');
  const [groupCount, setGroupCount] = useState(4);
  /** 组图：点击「张数」pill 后展开滑块浮层 */
  const [groupCountPopOpen, setGroupCountPopOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [referenceImages, setReferenceImages] = useState<UiImageRef[]>([]);
  const generations = useSeedanceImageGenStore((s) => s.generations);
  const setGenerations = useSeedanceImageGenStore((s) => s.setGenerations);
  const generationsHydrated = useSeedanceImageGenStore((s) => s.generationsHydrated);
  const loading = useSeedanceImageGenStore((s) => s.loading);
  const generatingPlaceholder = useSeedanceImageGenStore((s) => s.generatingPlaceholder);
  const genQueuePlaceholders = useSeedanceImageGenStore((s) => s.genQueuePlaceholders);
  const runGenerate = useSeedanceImageGenStore((s) => s.runGenerate);
  const hydrateImageGen = useSeedanceImageGenStore((s) => s.hydrate);
  /** 当前已渲染的生成记录条数（按视口估算首屏，滚动加载更多） */
  const [visibleGenCount, setVisibleGenCount] = useState(IMAGE_GEN_FEED_PAGE_SIZE_MIN);
  const feedInitialPageSizeRef = useRef(IMAGE_GEN_FEED_PAGE_SIZE_MIN);
  const feedLoadMorePageSizeRef = useRef(IMAGE_GEN_FEED_PAGE_SIZE_MIN);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [gridMoreOpenId, setGridMoreOpenId] = useState<string | null>(null);
  /** 网格视图下每条记录主预览对应的图片下标（组图切换） */
  const [gridPreviewIndex, setGridPreviewIndex] = useState<Record<string, number>>({});
  const [deleteConfirmRec, setDeleteConfirmRec] = useState<GenRecord | null>(null);
  const [retentionNoticeDismissed, setRetentionNoticeDismissed] = useState(
    () =>
      getLocalStorageValue(SEEDANCE_STORAGE_KEYS.imageGenRetentionNoticeDismissed) === '1',
  );

  // ----- 加入素材库弹框相关状态 -----
  const [libModalSrc, setLibModalSrc] = useState<string | null>(null);
  const [libModalRec, setLibModalRec] = useState<GenRecord | null>(null);
  const [libAssetName, setLibAssetName] = useState('');
  const [libGroupOptions, setLibGroupOptions] = useState<VirtualAvatarGroupOption[]>([]);
  const [libGroupsLoading, setLibGroupsLoading] = useState(false);
  const [selectedLibGroupId, setSelectedLibGroupId] = useState('');
  const [addingToLib, setAddingToLib] = useState(false);

  const refInputRef = useRef<HTMLInputElement>(null);
  const referenceImagesRef = useRef(referenceImages);
  referenceImagesRef.current = referenceImages;
  const paramsFloatRef = useRef<HTMLDivElement>(null);
  const groupCountPopRef = useRef<HTMLDivElement>(null);
  const genModeDockRef = useRef<HTMLDivElement>(null);
  const feedScrollRef = useRef<HTMLElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  /** 底栏工具条：生成方式下拉（点击展开，与分辨率 pill 同高） */
  const [genModeMenuOpen, setGenModeMenuOpen] = useState(false);
  const [dockMentionState, setDockMentionState] = useState<{ open: boolean; query: string }>({
    open: false,
    query: '',
  });
  const [dockMentionThumbHover, setDockMentionThumbHover] = useState<{
    thumbUrl: string | null;
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const promptDockTextareaRef = useRef<HTMLTextAreaElement>(null);
  const promptDockHighlightRef = useRef<HTMLDivElement>(null);
  const promptDockCursorRef = useRef(0);
  const mentionThumbHoverRaf = useRef<number | null>(null);

  const collapseDock = useCallback(() => {
    setDockExpanded(false);
    setParamsOpen(false);
    setGroupCountPopOpen(false);
    setGenModeMenuOpen(false);
    setDockMentionState({ open: false, query: '' });
    setDockMentionThumbHover(null);
  }, []);

  const dismissRetentionNotice = useCallback(() => {
    setRetentionNoticeDismissed(true);
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.imageGenRetentionNoticeDismissed, '1');
  }, []);

  const openAiGptImage = useMemo(() => isOpenAiGptImageModel(model), [model]);
  const openAiGptImageEdit = useMemo(() => supportsOpenAiGptImageEdit(model), [model]);
  const groupGenEnabled = useMemo(() => supportsImageGenGroupMode(model), [model]);

  useEffect(() => {
    if (!groupGenEnabled && genMode === 'group') {
      setGenMode('single');
      setGroupCountPopOpen(false);
      setGenModeMenuOpen(false);
    }
  }, [groupGenEnabled, genMode]);

  useEffect(() => {
    if (!openAiGptImage) return;
    if (!isValidOpenAiImageSizeForModel(model, openAiImageSize)) {
      setOpenAiImageSize('auto');
    }
  }, [model, openAiGptImage, openAiImageSize]);

  /** GPT Image 2 可多图参考；Gemini / GPT-img1 仅第一张有效 */
  const showRefThumbInMainBtn =
    Boolean(referenceImages.length === 1 && !openAiGptImageEdit);
  const showRefStrip =
    Boolean(openAiGptImageEdit && referenceImages.length > 0);
  /** GPT Image 2 参考图上限时隐藏「+」按钮；Gemini 等始终可点此钮以替换首张 */
  const canShowRefAddBtn =
    !openAiGptImage ||
    referenceImages.length < GPT_IMAGE_2_MAX_REFERENCE_IMAGES ||
    !openAiGptImageEdit;

  /** GPT Image 2 多参考图：提示词内 @图N 与左侧缩略图条顺序一致（提交时由 store 转为图N） */
  const mentionUiEnabled = Boolean(openAiGptImageEdit && referenceImages.length > 0);
  const mentionCandidates = useMemo<PromptMentionCandidate[]>(() => {
    if (!mentionUiEnabled) return [];
    return referenceImages.map((img, i) => {
      const label = buildPromptMentionLabel('image', i + 1);
      return {
        nodeId: img.id,
        name: `参考图 ${i + 1}`,
        label,
        mention: `@${label}`,
      };
    });
  }, [mentionUiEnabled, referenceImages]);

  const filteredDockMentionCandidates = useMemo(() => {
    if (!dockMentionState.open) return [];
    const q = dockMentionState.query.trim().toLowerCase();
    if (!q) return mentionCandidates;
    return mentionCandidates.filter(
      (c) => c.label.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [mentionCandidates, dockMentionState.open, dockMentionState.query]);

  useEffect(() => {
    if (!mentionUiEnabled) {
      setDockMentionState({ open: false, query: '' });
      setDockMentionThumbHover(null);
    }
  }, [mentionUiEnabled]);

  useLayoutEffect(() => {
    if (!mentionUiEnabled) return;
    const ta = promptDockTextareaRef.current;
    const hi = promptDockHighlightRef.current;
    if (!ta || !hi) return;
    return bindPromptHighlightOverlaySync(ta, hi);
  }, [mentionUiEnabled, dockExpanded]);

  useLayoutEffect(() => {
    if (!mentionUiEnabled) return;
    syncPromptHighlightScroll(promptDockTextareaRef.current, promptDockHighlightRef.current);
  }, [mentionUiEnabled, prompt]);

  useEffect(
    () => () => {
      if (mentionThumbHoverRaf.current != null) cancelAnimationFrame(mentionThumbHoverRaf.current);
    },
    [],
  );

  /** GPT Image 1 不支持图生图：切换到该模型时清除参考图 */
  useEffect(() => {
    if (!openAiGptImage || openAiGptImageEdit) return;
    setReferenceImages((prev) => {
      if (!prev.length) return prev;
      if (refInputRef.current) refInputRef.current.value = '';
      return [];
    });
  }, [model, openAiGptImage, openAiGptImageEdit]);

  /** 非 GPT Image 2 仅存首张：从 GPT Image 2 多端切至其他模型时截断保留第一张 */
  useEffect(() => {
    if (openAiGptImageEdit) return;
    setReferenceImages((prev) => {
      if (prev.length <= 1) return prev;
      showToast('当前模型仅使用首张参考图，已为您保留第一张', 'i');
      return prev.slice(0, 1);
    });
  }, [openAiGptImageEdit, showToast]);

  /** 组图仅支持最高 2K，与 4K 互斥（仅 Gemini 2.5 组图） */
  useEffect(() => {
    if (groupGenEnabled && genMode === 'group' && resolutionUi === '4K') {
      setResolutionUi('2K');
    }
  }, [groupGenEnabled, genMode, resolutionUi]);

  /** 画面参数浮层：点击面板外关闭（触发按钮/底栏 pill 除外，由各自 onClick 切换） */
  useEffect(() => {
    if (!paramsOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (paramsFloatRef.current?.contains(t)) return;
      if (t instanceof Element && t.closest('[data-ig-params-anchor]')) return;
      setParamsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [paramsOpen]);

  useEffect(() => {
    if (paramsOpen) {
      setGroupCountPopOpen(false);
      setGenModeMenuOpen(false);
    }
  }, [paramsOpen]);

  useEffect(() => {
    if (groupCountPopOpen) {
      setParamsOpen(false);
      setGenModeMenuOpen(false);
    }
  }, [groupCountPopOpen]);

  useEffect(() => {
    if (genModeMenuOpen) {
      setParamsOpen(false);
      setGroupCountPopOpen(false);
    }
  }, [genModeMenuOpen]);

  /** 组图张数浮层：点击外部关闭 */
  useEffect(() => {
    if (!groupCountPopOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (groupCountPopRef.current?.contains(t)) return;
      setGroupCountPopOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [groupCountPopOpen]);

  /** 底栏生成方式菜单：点击外部关闭 */
  useEffect(() => {
    if (!genModeMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (genModeDockRef.current?.contains(t)) return;
      setGenModeMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [genModeMenuOpen]);

  useEffect(() => {
    if (genMode === 'single') setGroupCountPopOpen(false);
  }, [genMode]);

  useEffect(() => {
    void hydrateImageGen();
  }, [hydrateImageGen]);

  const sortedGenerations = useMemo(() => {
    return [...generations].sort((a, b) => b.createdAt - a.createdAt);
  }, [generations]);

  const visibleGenerations = useMemo(
    () => sortedGenerations.slice(0, visibleGenCount),
    [sortedGenerations, visibleGenCount],
  );

  const hasMoreGenerations = visibleGenCount < sortedGenerations.length;

  const measureFeedPageSize = useCallback(
    (mode: 'initial' | 'more') => {
      const root = feedScrollRef.current;
      const view = viewMode;
      const fallbackWidth =
        typeof window !== 'undefined' ? Math.min(window.innerWidth - 48, 1200) : 960;
      const fallbackHeight = typeof window !== 'undefined' ? window.innerHeight * 0.55 : 480;

      if (!root) {
        const size = mode === 'initial' ? 12 : 8;
        return size;
      }

      const pad =
        parseFloat(getComputedStyle(root).paddingTop || '0') +
        parseFloat(getComputedStyle(root).paddingBottom || '0');
      const noticeH =
        root.querySelector('.ig-retention-notice')?.getBoundingClientRect().height ?? 0;
      const containerWidth = Math.max(200, root.clientWidth - pad);
      const containerHeight = Math.max(200, root.clientHeight);

      if (mode === 'initial') {
        return computeImageGenFeedInitialPageSize({
          viewMode: view,
          containerWidth: containerWidth || fallbackWidth,
          containerHeight: containerHeight || fallbackHeight,
          reservedTopPx: noticeH + 8,
        });
      }
      return computeImageGenFeedLoadMorePageSize(view, containerWidth || fallbackWidth);
    },
    [viewMode],
  );

  const applyInitialFeedPageSize = useCallback(() => {
    const initial = measureFeedPageSize('initial');
    const more = measureFeedPageSize('more');
    feedInitialPageSizeRef.current = initial;
    feedLoadMorePageSizeRef.current = more;
    setVisibleGenCount(() => {
      const total = sortedGenerations.length;
      if (!total) return initial;
      return Math.min(initial, total);
    });
  }, [measureFeedPageSize, sortedGenerations.length]);

  const loadMoreGenerations = useCallback(() => {
    const step = feedLoadMorePageSizeRef.current || measureFeedPageSize('more');
    setVisibleGenCount((c) => Math.min(c + step, sortedGenerations.length));
  }, [measureFeedPageSize, sortedGenerations.length]);

  useEffect(() => {
    setVisibleGenCount((c) => Math.min(c, sortedGenerations.length));
  }, [sortedGenerations.length]);

  useEffect(() => {
    if (!generationsHydrated) return;
    const t = window.setTimeout(() => applyInitialFeedPageSize(), 0);
    return () => window.clearTimeout(t);
  }, [generationsHydrated, viewMode, applyInitialFeedPageSize]);

  useEffect(() => {
    if (!generationsHydrated) return;
    const root = feedScrollRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    let tid: number | undefined;
    const ro = new ResizeObserver(() => {
      if (tid !== undefined) window.clearTimeout(tid);
      tid = window.setTimeout(() => {
        const initial = measureFeedPageSize('initial');
        const more = measureFeedPageSize('more');
        feedInitialPageSizeRef.current = initial;
        feedLoadMorePageSizeRef.current = more;
      }, 200);
    });
    ro.observe(root);
    return () => {
      ro.disconnect();
      if (tid !== undefined) window.clearTimeout(tid);
    };
  }, [generationsHydrated, measureFeedPageSize, viewMode]);

  useEffect(() => {
    const root = feedScrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!generationsHydrated || !root || !sentinel || !hasMoreGenerations) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMoreGenerations();
      },
      { root, rootMargin: '280px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [generationsHydrated, hasMoreGenerations, loadMoreGenerations, visibleGenerations.length]);

  /** 按时间倒序摊平所有图片，用于大图左右切换 */
  const lightboxGallery = useMemo(() => {
    const items: { src: string; rec: GenRecord }[] = [];
    for (const rec of sortedGenerations) {
      for (const src of rec.images) {
        items.push({ src, rec });
      }
    }
    return items;
  }, [sortedGenerations]);

  const lightboxSrc =
    lightboxIndex != null ? (lightboxGallery[lightboxIndex]?.src ?? null) : null;
  const lightboxTotal = lightboxGallery.length;
  const canLightboxPrev = lightboxIndex != null && lightboxIndex > 0;
  const canLightboxNext = lightboxIndex != null && lightboxIndex < lightboxTotal - 1;

  const lightboxActiveItem = useMemo(() => {
    if (lightboxIndex == null) return null;
    return lightboxGallery[lightboxIndex] ?? null;
  }, [lightboxGallery, lightboxIndex]);

  const openLightbox = useCallback(
    (src: string) => {
      const idx = lightboxGallery.findIndex((x) => x.src === src);
      if (idx >= 0) setLightboxIndex(idx);
    },
    [lightboxGallery],
  );

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (lightboxIndex == null) return;
    if (!lightboxGallery[lightboxIndex]) closeLightbox();
  }, [lightboxGallery, lightboxIndex, closeLightbox]);

  useEffect(() => {
    if (gridMoreOpenId == null) return;
    const onDoc = () => setGridMoreOpenId(null);
    const t = window.setTimeout(() => document.addEventListener('click', onDoc), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('click', onDoc);
    };
  }, [gridMoreOpenId]);

  const removeReferenceImage = useCallback(
    (id: string) => {
      const prev = referenceImagesRef.current;
      const idx = prev.findIndex((x) => x.id === id);
      if (idx === -1) return;

      const removedNum = idx + 1;
      setReferenceImages(prev.filter((x) => x.id !== id));
      if (openAiGptImageEdit) {
        setPrompt((p) => adjustPromptMentionsAfterSlotRemoved(p, 'image', removedNum));
      }
    },
    [openAiGptImageEdit],
  );

  const clearReferenceImages = useCallback(() => {
    const prev = referenceImagesRef.current;
    if (openAiGptImageEdit && prev.length > 0) {
      setPrompt((p) => {
        let text = p;
        for (let n = prev.length; n >= 1; n -= 1) {
          text = adjustPromptMentionsAfterSlotRemoved(text, 'image', n);
        }
        return text;
      });
    }
    setReferenceImages([]);
    if (refInputRef.current) refInputRef.current.value = '';
  }, [openAiGptImageEdit]);

  const onReferenceInputChange = async (files: FileList | null | undefined) => {
    const input = refInputRef.current;
    try {
      const listRaw = Array.from(files ?? []).filter((f) => f.type.startsWith('image/'));
      if (!listRaw.length) return;

      const built: UiImageRef[] = [];
      for (const file of listRaw) {
        const rec = await readFileAsUiImageRef(file);
        if (rec) built.push(rec);
      }
      if (!built.length) return;

      if (openAiGptImageEdit) {
        setReferenceImages((prev) => {
          const room = Math.max(0, GPT_IMAGE_2_MAX_REFERENCE_IMAGES - prev.length);
          if (room === 0) {
            showToast(`GPT Image 2 最多添加 ${GPT_IMAGE_2_MAX_REFERENCE_IMAGES} 张参考图`, 'i');
            return prev;
          }
          const add = built.slice(0, room);
          if (built.length > room) {
            showToast(
              `已选 ${built.length} 张，仅能再添加 ${room} 张（上限 ${GPT_IMAGE_2_MAX_REFERENCE_IMAGES} 张）`,
              'i',
            );
          }
          return [...prev, ...add].slice(0, GPT_IMAGE_2_MAX_REFERENCE_IMAGES);
        });
      } else {
        const first = built[0];
        if (built.length > 1) {
          showToast('当前模型单次仅使用一张参考图，已保留第一张', 'i');
        }
        setReferenceImages(first ? [first] : []);
      }
    } finally {
      if (input) input.value = '';
    }
  };

  const applyDockPromptMention = useCallback((candidate: PromptMentionCandidate) => {
    const ta = promptDockTextareaRef.current;
    const fallback = ta?.selectionStart ?? promptDockCursorRef.current;
    const cursor = typeof fallback === 'number' ? fallback : 0;
    setPrompt((prev) => {
      const before = prev.slice(0, cursor);
      const after = prev.slice(cursor);
      const replacedBefore = before.replace(/@([^\s@]*)$/, `${candidate.mention} `);
      const next = replacedBefore + after;
      const nextCursor = replacedBefore.length;
      promptDockCursorRef.current = nextCursor;
      window.setTimeout(() => {
        const el = promptDockTextareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      }, 0);
      return next;
    });
    setDockMentionState({ open: false, query: '' });
  }, []);

  const handleDockPromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value;
      const cursorPos = e.target.selectionStart ?? text.length;
      promptDockCursorRef.current = cursorPos;
      setPrompt(text);
      if (!mentionUiEnabled) return;
      const before = text.slice(0, cursorPos);
      const m = /@([^\s@]*)$/.exec(before);
      if (!m || mentionCandidates.length === 0) {
        setDockMentionState((s) => (s.open ? { open: false, query: '' } : s));
        return;
      }
      setDockMentionState({ open: true, query: m[1] ?? '' });
    },
    [mentionCandidates.length, mentionUiEnabled],
  );

  const flushDockMentionThumbHover = useCallback(
    (ta: HTMLTextAreaElement, clientX: number, clientY: number) => {
      if (mentionCandidates.length === 0) {
        setDockMentionThumbHover(null);
        return;
      }
      const text = ta.value;
      if (!promptTextHasMediaRefSegments(text)) {
        setDockMentionThumbHover(null);
        return;
      }
      const idx = text.length > 8000 ? -1 : getTextareaIndexNearPoint(ta, clientX, clientY);
      const refAt = idx >= 0 ? getPromptMentionAtIndex(text, idx) : null;
      if (refAt == null || refAt.kind !== 'image') {
        setDockMentionThumbHover(null);
        return;
      }
      const cand = findPromptMentionCandidate(mentionCandidates, refAt);
      const label = cand
        ? cand.mention
        : `@${buildPromptMentionLabel(refAt.kind, refAt.num)}`;
      if (!cand) {
        setDockMentionThumbHover({ thumbUrl: null, label, x: clientX, y: clientY });
        return;
      }
      const img = referenceImages.find((r) => r.id === cand.nodeId);
      const thumbUrl = (img?.thumb || '').trim() || null;
      setDockMentionThumbHover({ thumbUrl, label, x: clientX, y: clientY });
    },
    [mentionCandidates, referenceImages],
  );

  const onDockPromptMouseMoveMentionThumb = useCallback(
    (e: React.MouseEvent<HTMLTextAreaElement>) => {
      if (!mentionUiEnabled) return;
      const ta = e.currentTarget;
      const cx = e.clientX;
      const cy = e.clientY;
      if (mentionThumbHoverRaf.current != null) cancelAnimationFrame(mentionThumbHoverRaf.current);
      mentionThumbHoverRaf.current = requestAnimationFrame(() => {
        mentionThumbHoverRaf.current = null;
        if (!ta.isConnected) return;
        flushDockMentionThumbHover(ta, cx, cy);
      });
    },
    [flushDockMentionThumbHover, mentionUiEnabled],
  );

  const onDockPromptMouseLeaveMentionThumb = useCallback(() => {
    if (mentionThumbHoverRaf.current != null) {
      cancelAnimationFrame(mentionThumbHoverRaf.current);
      mentionThumbHoverRaf.current = null;
    }
    setDockMentionThumbHover(null);
  }, []);

  const reloadGenRecordIntoDock = useCallback((rec: GenRecord) => {
    applyGenRecordModelConfig(rec, {
      setModel,
      setResolutionUi,
      setAspectUi,
      setOpenAiImageSize,
      setGenMode,
      setGroupCount,
    });
    setPrompt(rec.prompt);
    setReferenceImages(genRecordReferenceInputsToUiRefs(rec.referenceInputs));
    setDockExpanded(true);
    if (refInputRef.current) refInputRef.current.value = '';
  }, []);

  /** 重新生成：始终使用该条记录保存的模型与参数，而非当前顶栏选择 */
  const buildRunGenerateParamsFromRecord = useCallback(
    (rec: GenRecord) => {
      const { genMode: gm, groupCount: gc } = resolveRecordGenMode(rec);
      return {
        promptText: rec.prompt,
        model: isRecordedImageGenModel(rec.model) ? rec.model.trim() : model,
        resolutionUi: resolutionSet.has(rec.resolution) ? rec.resolution : resolutionUi,
        aspectUi: aspectSet.has(rec.aspect) ? rec.aspect : aspectUi,
        genMode: gm,
        groupCount: gc,
        openAiImageSize: resolveOpenAiImageSizeFromRecord(rec),
        referenceFiles: referenceInputsToRunPayload(rec.referenceInputs),
      };
    },
    [model, resolutionUi, aspectUi],
  );

  const buildRunGenerateParams = useCallback(
    (promptText: string) => ({
      promptText,
      model,
      resolutionUi,
      aspectUi,
      genMode,
      groupCount,
      openAiImageSize,
      referenceFiles: referenceImages.map(({ base64, mime }) => ({ base64, mime })),
    }),
    [
      model,
      resolutionUi,
      aspectUi,
      genMode,
      groupCount,
      openAiImageSize,
      referenceImages,
    ],
  );

  const onSubmit = () => void runGenerate(buildRunGenerateParams(prompt));

  /** 重新生成：请求用记录内参数，并同步顶栏模型 / 画面配置 */
  const regenerateFromRecord = useCallback(
    (rec: GenRecord) => {
      applyGenRecordModelConfig(rec, {
        setModel,
        setResolutionUi,
        setAspectUi,
        setOpenAiImageSize,
        setGenMode,
        setGroupCount,
      });
      void runGenerate(buildRunGenerateParamsFromRecord(rec));
    },
    [buildRunGenerateParamsFromRecord, runGenerate],
  );

  const captionModel = (m: string) =>
    IMAGE_GEN_MODEL_OPTIONS.find((x) => x.value === m)?.caption ?? m;

  // ----- 加入素材库弹框逻辑 -----
  const hasLibraryAuth = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const loadLibGroupOptions = useCallback(async () => {
    if (!hasLibraryAuth()) {
      setLibGroupOptions([]);
      setSelectedLibGroupId('');
      return;
    }
    setLibGroupsLoading(true);
    try {
      const customGroupId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.customGroupId).trim();
      let info;
      if (customGroupId) {
        info = {
          groupId: customGroupId,
        };
      } else {
        info = await getUserAvatarAssetGroup({
          onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
        });
      }
      const options = buildVirtualAvatarGroupOptions(info as any);
      setLibGroupOptions(options);
      const current = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.librarySelectedGroupId).trim();
      setSelectedLibGroupId(resolveVirtualAvatarGroupSelection(options, current));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`获取虚拟人像素材组失败: ${msg}`, true);
      setLibGroupOptions([]);
      setSelectedLibGroupId('');
    } finally {
      setLibGroupsLoading(false);
    }
  }, [addDebug, hasLibraryAuth]);

  const openAddToLibrary = (src: string, rec: GenRecord) => {
    closeLightbox();
    setLibModalSrc(src);
    setLibModalRec(rec);
    setLibAssetName(defaultLibAssetNameFromRec(rec));
    void loadLibGroupOptions();
  };

  const closeAddToLibrary = () => {
    if (addingToLib) return;
    setLibModalSrc(null);
    setLibModalRec(null);
    setLibAssetName('');
  };

  const submitAddToLibrary = useCallback(async () => {
    const groupId = selectedLibGroupId.trim();
    if (!libModalSrc || !libModalRec || !groupId) return;
    if (!hasLibraryAuth()) {
      showToast('请先在「素材库」页配置系统访问令牌和用户 ID', 'e');
      return;
    }
    setAddingToLib(true);
    const fallbackDisplay =
      libModalRec.prompt.trim().slice(0, 200) || `生成记录_${libModalRec.id.slice(0, 8)}`;
    const assetNameForApi = libAssetName.trim() || fallbackDisplay;
    const fileStem = stemForLibraryUploadFile(assetNameForApi);
    try {
      let publicUrl = libModalSrc;
      if (libModalSrc.startsWith('data:') || /^blob:/i.test(libModalSrc)) {
        addDebug('图片为 data/blob URL，先批量上传到 OSS 取公网地址');
        const file = await fetchImageAsFile(libModalSrc, fileStem);
        const urls = await batchUploadImagesToOss([file], {
          onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
        });
        publicUrl = urls[0];
      }
      await createVirtualPortrait(
        {
          group_id: groupId,
          url: publicUrl,
          name: assetNameForApi.slice(0, 200),
          asset_type: VP_ASSET_TYPE_IMAGE,
        },
        { onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) },
      );
      setLocalStorageValue(SEEDANCE_STORAGE_KEYS.librarySelectedGroupId, groupId);
      const groupLabel =
        libGroupOptions.find((o) => o.groupId === groupId)?.label ?? '素材组';
      showToast(`已加入${groupLabel}`, 's');
      setLibModalSrc(null);
      setLibModalRec(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`加入素材库失败: ${msg}`, true);
      showToast('加入素材库失败: ' + msg, 'e');
    } finally {
      setAddingToLib(false);
    }
  }, [addDebug, hasLibraryAuth, libAssetName, libGroupOptions, libModalRec, libModalSrc, selectedLibGroupId, showToast]);

  // ----- 渲染单图悬停 overlay（下载 / 加入素材库） -----
  const renderImageOverlay = (src: string, rec: GenRecord) => (
    <div className="ig-img-overlay">
      <button
        type="button"
        className="ig-overlay-btn"
        title="下载"
        onClick={(e) => {
          e.stopPropagation();
          const fname =
            (rec.prompt.trim().slice(0, 16).replace(/\s+/g, '_') || `gen_${rec.id}`) + '.png';
          triggerDownload(src, fname);
        }}
      >
        <span aria-hidden>⤓</span>
      </button>
      <button
        type="button"
        className="ig-overlay-btn"
        title="加入素材库"
        onClick={(e) => {
          e.stopPropagation();
          openAddToLibrary(src, rec);
        }}
      >
        <span aria-hidden>📦</span>
      </button>
    </div>
  );

  return (
    <div className="canvas-wrap image-gen-page">
      <div className="ig-shell">
        <header className="ig-topbar">
          <div className="ig-topbar-left">
            <select
              className="ig-select ig-model-select"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              aria-label="模型"
            >
              {IMAGE_GEN_MODEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {'recommended' in o && o.recommended ? `${o.label}（推荐）` : o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`ig-btn ig-btn-ghost${paramsOpen ? ' active' : ''}`}
              data-ig-params-anchor
              onClick={() => setParamsOpen((v) => !v)}
            >
              <span className="ig-ico-sliders" aria-hidden />
              模型参数
            </button>
          </div>
          <div className="ig-topbar-right">
            <div className="ig-view-toggle" role="group" aria-label="视图">
              <button
                type="button"
                className={viewMode === 'list' ? 'on' : ''}
                onClick={() => setViewMode('list')}
                title="列表"
              >
                ▤
              </button>
              <button
                type="button"
                className={viewMode === 'grid' ? 'on' : ''}
                onClick={() => setViewMode('grid')}
                title="网格"
              >
                ▦
              </button>
            </div>
          </div>
        </header>

        {paramsOpen ? (
          <div className="ig-params-float" ref={paramsFloatRef}>
            <div className="ig-params-head">
              <span>画面参数</span>
              <button type="button" className="ig-params-close" onClick={() => setParamsOpen(false)}>
                ✕
              </button>
            </div>
            <div className="ig-params-row">
              <span className="ig-params-label">生成方式</span>
              <div className="ig-seg ig-seg--resolution">
                <button
                  type="button"
                  className={genMode === 'single' ? 'on' : ''}
                  onClick={() => setGenMode('single')}
                >
                  单张
                </button>
                <button
                  type="button"
                  className={genMode === 'group' ? 'on' : ''}
                  disabled={!groupGenEnabled}
                  title={groupGenEnabled ? undefined : '组图暂仅支持 Gemini 2.5 Flash Image'}
                  onClick={() => {
                    if (!groupGenEnabled) return;
                    setGenMode('group');
                  }}
                >
                  组图
                </button>
              </div>
            </div>
            {groupGenEnabled && genMode === 'group' ? (
              <div className="ig-params-row">
                <span className="ig-params-label">生成张数</span>
                <div className="ig-params-count-row">
                  <input
                    type="range"
                    className="ig-params-count-range"
                    min={2}
                    max={4}
                    step={1}
                    value={groupCount}
                    onChange={(e) => setGroupCount(Number(e.target.value))}
                    aria-valuemin={2}
                    aria-valuemax={4}
                    aria-valuenow={groupCount}
                    aria-label="生成张数"
                  />
                  <span className="ig-params-count-display" aria-hidden>
                    {groupCount}
                  </span>
                </div>
                <p className="ig-params-count-hint">上限 4 张</p>
              </div>
            ) : null}
            {openAiGptImage ? (
              <div className="ig-params-row">
                <span className="ig-params-label">图片大小</span>
                <select
                  className="ig-select"
                  value={openAiImageSize}
                  onChange={(e) => setOpenAiImageSize(e.target.value as OpenAiGptImageSizeChoice)}
                  aria-label="图片大小（宽×高）"
                >
                  {openAiImageSizeOptionsForModel(model).map((s) => (
                    <option key={s} value={s}>
                      {formatOpenAiImageSizeOptionLabel(model, s)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div className="ig-params-row">
                  <span className="ig-params-label">分辨率</span>
                  <div className="ig-seg ig-seg--resolution">
                    {RESOLUTION_OPTIONS.map((r) => {
                      const dis4kGroup = groupGenEnabled && genMode === 'group' && r === '4K';
                      const disabled = dis4kGroup;
                      const title = dis4kGroup ? '组图最高支持 2K' : undefined;
                      return (
                        <button
                          key={r}
                          type="button"
                          disabled={disabled}
                          title={title}
                          className={resolutionUi === r ? 'on' : ''}
                          onClick={() => {
                            if (disabled) return;
                            setResolutionUi(r);
                          }}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="ig-params-row ig-aspect-row">
                  <span className="ig-params-label">图片比例</span>
                  <div className="ig-aspect-chips">
                    {ASPECT_OPTIONS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className={aspectUi === a ? 'on' : ''}
                        onClick={() => setAspectUi(a)}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}

        <main ref={feedScrollRef} className={`ig-feed ig-feed--${viewMode}`}>
          {!generationsHydrated ? (
            <div
              className="ig-feed-hydrate-overlay"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="ig-feed-hydrate-card">
                <div className="ig-feed-hydrate-spinner" aria-hidden />
                <p className="ig-feed-hydrate-label">加载生成记录…</p>
              </div>
            </div>
          ) : null}
          {generationsHydrated &&
          sortedGenerations.length > 0 &&
          !generatingPlaceholder &&
          !retentionNoticeDismissed ? (
            <aside className="ig-retention-notice" role="note">
              <span className="ig-retention-notice-ico" aria-hidden>
                i
              </span>
              <p className="ig-retention-notice-text">{IMAGE_GEN_STORAGE_HINT}</p>
              <button
                type="button"
                className="ig-retention-notice-close"
                onClick={dismissRetentionNotice}
                aria-label="关闭提示"
                title="关闭"
              >
                ✕
              </button>
            </aside>
          ) : null}
          {!sortedGenerations.length && !generatingPlaceholder ? (
            <div className="ig-empty">
              <p>暂无生成记录</p>
              <p className="ig-empty-sub">在底部输入描述并上传参考图（可选），即可开始生成。</p>
              {!retentionNoticeDismissed ? (
                <div className="ig-empty-retention">
                  <p>{IMAGE_GEN_STORAGE_HINT}</p>
                  <button
                    type="button"
                    className="ig-retention-notice-close"
                    onClick={dismissRetentionNotice}
                    aria-label="关闭提示"
                    title="关闭"
                  >
                    ✕
                  </button>
                </div>
              ) : null}
            </div>
          ) : viewMode === 'grid' ? (
            <>
              {generatingPlaceholder ? (
                <article
                  key={generatingPlaceholder.id}
                  className="ig-card ig-card--pending"
                  aria-busy="true"
                >
                  <div className="ig-card-visual">
                    <div className="ig-generating-placeholder">
                      <span className="ig-generating-ico" aria-hidden />
                      <span className="ig-generating-text">
                        {generatingPlaceholder.batchCount != null &&
                        generatingPlaceholder.batchCount > 1
                          ? `图片生成中（${generatingPlaceholder.batchCount} 张${
                              generatingPlaceholder.batchParallel ? '·并行' : ''
                            }）`
                          : '图片生成中'}
                      </span>
                    </div>
                    <span className="ig-badge-model">
                      {captionModel(generatingPlaceholder.model)}
                    </span>
                  </div>
                  <p className="ig-card-caption">{generatingPlaceholder.prompt}</p>
                  <div className="ig-card-meta">生成中…</div>
                </article>
              ) : null}
              {genQueuePlaceholders.map((qp, qi) => (
                <article
                  key={qp.id}
                  className="ig-card ig-card--queued"
                  aria-label="排队等待生成"
                >
                  <div className="ig-card-visual">
                    <div className="ig-generating-placeholder ig-generating-placeholder--queued">
                      <span className="ig-queued-num" aria-hidden>{qi + 1}</span>
                      <span className="ig-generating-text">
                        {qp.batchCount != null && qp.batchCount > 1
                          ? `等待中（${qp.batchCount} 张）`
                          : '等待中'}
                      </span>
                    </div>
                    <span className="ig-badge-model">{captionModel(qp.model)}</span>
                  </div>
                  <p className="ig-card-caption">{qp.prompt}</p>
                  <div className="ig-card-meta">队列第 {qi + 2} 个</div>
                </article>
              ))}
              {visibleGenerations.map((rec) => {
                const imgLen = rec.images.length;
                const rawIdx = gridPreviewIndex[rec.id] ?? 0;
                const mainIdx =
                  imgLen > 0 ? Math.min(Math.max(0, rawIdx), imgLen - 1) : 0;
                const mainSrc = rec.images[mainIdx] ?? rec.images[0];
                const failed = imgLen === 0;
                const failMsg = failed
                  ? rec.generationError?.trim() || '生成未完成，暂无图片'
                  : '';
                return (
              <article key={rec.id} className={`ig-card${failed ? ' ig-card--failed' : ''}`}>
                <div className="ig-card-visual">
                  {mainSrc ? (
                    <>
                      <button
                        type="button"
                        className="ig-gen-preview-hit"
                        onClick={() => openLightbox(mainSrc)}
                        title="查看大图"
                        aria-label="查看大图"
                      >
                        <img
                          src={mainSrc}
                          alt=""
                          referrerPolicy="no-referrer"
                          loading="lazy"
                        />
                      </button>
                      {renderImageOverlay(mainSrc, rec)}
                    </>
                  ) : failed ? (
                    <div className="ig-gen-failed-slot">
                      <span className="ig-gen-failed-label">生成失败</span>
                      <p className="ig-gen-failed-msg">{failMsg}</p>
                    </div>
                  ) : (
                    <div className="ig-card-ph">无图</div>
                  )}
                  <span className="ig-badge-model">{captionModel(rec.model)}</span>
                  {!failed ? (
                    <span className="ig-watermark">AI生成</span>
                  ) : null}
                  {mainSrc ? (
                    <div
                      className="ig-grid-hover-bar"
                      role="group"
                      aria-label="快捷操作"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="ig-grid-hover-bar-inner">
                        <div className="ig-grid-hover-actions">
                          <button
                            type="button"
                            className="ig-grid-hover-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              reloadGenRecordIntoDock(rec);
                              showToast(
                                rec.referenceInputs?.length
                                  ? '已载入该次生成使用的提示词与参考图，可修改后再生成'
                                  : '已载入提示词，可修改后重新生成',
                                'i',
                              );
                            }}
                          >
                            <span className="ig-grid-hover-ico" aria-hidden>
                              ✎
                            </span>
                            重新编辑
                          </button>
                          <span className="ig-grid-hover-divider" aria-hidden />
                          <button
                            type="button"
                            className="ig-grid-hover-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              regenerateFromRecord(rec);
                            }}
                          >
                            <span className="ig-grid-hover-ico" aria-hidden>
                              ⟳
                            </span>
                            重新生成
                          </button>
                        </div>
                        <div className="ig-grid-hover-more">
                          <button
                            type="button"
                            className="ig-grid-hover-more-btn"
                            aria-expanded={gridMoreOpenId === rec.id}
                            aria-haspopup="menu"
                            title="更多"
                            onClick={(e) => {
                              e.stopPropagation();
                              setGridMoreOpenId((cur) => (cur === rec.id ? null : rec.id));
                            }}
                          >
                            <span aria-hidden>⋮</span>
                          </button>
                          {gridMoreOpenId === rec.id ? (
                            <div className="ig-grid-more-menu" role="menu">
                              <button
                                type="button"
                                role="menuitem"
                                className="ig-grid-more-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGridMoreOpenId(null);
                                  setDeleteConfirmRec(rec);
                                }}
                              >
                                <svg
                                  className="ig-grid-more-trash"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  aria-hidden
                                >
                                  <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />
                                </svg>
                                删除
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                {rec.images.length > 1 ? (
                  <div className="ig-thumb-strip" role="tablist" aria-label="切换预览图">
                    {rec.images.map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        role="tab"
                        aria-selected={i === mainIdx}
                        className={`ig-thumb-mini${i === mainIdx ? ' ig-thumb-mini--active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setGridPreviewIndex((m) => ({ ...m, [rec.id]: i }));
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          openLightbox(src);
                        }}
                        title="单击切换预览，双击看大图"
                      >
                        <img src={src} alt="" />
                      </button>
                    ))}
                  </div>
                ) : null}
                <p className="ig-card-caption">{rec.prompt}</p>
                <div className="ig-card-actions">
                  <button
                    type="button"
                    className="ig-mini-btn"
                    onClick={() => {
                      reloadGenRecordIntoDock(rec);
                      showToast(
                        rec.referenceInputs?.length
                          ? '已载入该次生成使用的提示词与参考图，可修改后再生成'
                          : '已载入提示词，可修改后重新生成',
                        'i',
                      );
                    }}
                  >
                    ✎ 重新编辑
                  </button>
                  <button
                    type="button"
                    className="ig-mini-btn"
                    onClick={() =>
                      regenerateFromRecord(rec)
                    }
                  >
                    ⟳ 重新生成
                  </button>
                  {failed ? (
                    <div className="ig-list-more">
                      <button
                        type="button"
                        className="ig-mini-btn ig-mini-btn--icon"
                        aria-expanded={gridMoreOpenId === rec.id}
                        aria-haspopup="menu"
                        title="更多"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGridMoreOpenId((cur) => (cur === rec.id ? null : rec.id));
                        }}
                      >
                        <span aria-hidden>⋯</span>
                      </button>
                      {gridMoreOpenId === rec.id ? (
                        <div className="ig-grid-more-menu ig-gen-more-menu--below" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className="ig-grid-more-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setGridMoreOpenId(null);
                              setDeleteConfirmRec(rec);
                            }}
                          >
                            <svg
                              className="ig-grid-more-trash"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              aria-hidden
                            >
                              <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />
                            </svg>
                            删除
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </article>
            );
            })}
              {hasMoreGenerations ? (
                <div
                  ref={loadMoreSentinelRef}
                  className="ig-feed-load-more"
                  role="status"
                  aria-live="polite"
                >
                  <span className="ig-feed-load-more-text">继续向下滚动加载更多…</span>
                </div>
              ) : null}
            </>
          ) : (
            <>
              {generatingPlaceholder ? (
                <article
                  key={generatingPlaceholder.id}
                  className="ig-list-item ig-list-item--pending"
                  aria-busy="true"
                >
                  <div className="ig-list-meta">生成中…</div>
                  <h3 className="ig-list-title">{generatingPlaceholder.prompt}</h3>
                  <div className="ig-list-tags">
                    {generatingPlaceholder.imageSize ? (
                      <span className="ig-tag">{generatingPlaceholder.imageSize}</span>
                    ) : (
                      <>
                        <span className="ig-tag">{generatingPlaceholder.resolution}</span>
                        <span className="ig-tag">{generatingPlaceholder.aspect}</span>
                      </>
                    )}
                    <span className="ig-tag ig-tag-model">
                      {captionModel(generatingPlaceholder.model)}
                    </span>
                  </div>
                  <div className="ig-list-images">
                    <div className="ig-list-image-wrap ig-preview-slot">
                      <div className="ig-generating-placeholder ig-generating-placeholder--fill-slot">
                        <span className="ig-generating-ico" aria-hidden />
                        <span className="ig-generating-text">
                          {generatingPlaceholder.batchCount != null &&
                          generatingPlaceholder.batchCount > 1
                            ? `图片生成中（${generatingPlaceholder.batchCount} 张${
                                generatingPlaceholder.batchParallel ? '·并行' : ''
                              }）`
                            : '图片生成中'}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              ) : null}
              {genQueuePlaceholders.map((qp, qi) => (
                <article
                  key={qp.id}
                  className="ig-list-item ig-list-item--queued"
                  aria-label="排队等待生成"
                >
                  <div className="ig-list-meta">队列第 {qi + 2} 个</div>
                  <h3 className="ig-list-title">{qp.prompt}</h3>
                  <div className="ig-list-tags">
                    {qp.imageSize ? (
                      <span className="ig-tag">{qp.imageSize}</span>
                    ) : (
                      <>
                        <span className="ig-tag">{qp.resolution}</span>
                        <span className="ig-tag">{qp.aspect}</span>
                      </>
                    )}
                    {qp.batchCount != null && qp.batchCount > 1 ? (
                      <span className="ig-tag">{qp.batchCount}张</span>
                    ) : null}
                    <span className="ig-tag ig-tag-model">{captionModel(qp.model)}</span>
                  </div>
                  <div className="ig-list-images">
                    <div className="ig-list-image-wrap ig-preview-slot">
                      <div className="ig-generating-placeholder ig-generating-placeholder--fill-slot ig-generating-placeholder--queued">
                        <span className="ig-queued-num" aria-hidden>{qi + 1}</span>
                        <span className="ig-generating-text">
                          {qp.batchCount != null && qp.batchCount > 1
                            ? `等待中（${qp.batchCount} 张）`
                            : '等待中'}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
              {visibleGenerations.map((rec) => {
                const failed = rec.images.length === 0;
                return (
              <article
                key={rec.id}
                className={`ig-list-item${failed ? ' ig-list-item--failed' : ''}`}
              >
                <h3 className="ig-list-title">{rec.prompt}</h3>
                <div className="ig-list-tags">
                  {failed ? (
                    <span className="ig-tag ig-tag-failed">失败</span>
                  ) : null}
                  {rec.imageSize ? (
                    <span className="ig-tag">{rec.imageSize}</span>
                  ) : (
                    <>
                      <span className="ig-tag">{rec.resolution}</span>
                      <span className="ig-tag">{rec.aspect}</span>
                    </>
                  )}
                  {rec.images.length > 1 ? (
                    <span className="ig-tag">{rec.images.length}张</span>
                  ) : null}
                  <span className="ig-tag ig-tag-model">{captionModel(rec.model)}</span>
                </div>
                <div className="ig-list-images">
                  {rec.images.length > 0 ? (
                    rec.images.map((src, i) => (
                    <div key={i} className="ig-list-image-wrap ig-preview-slot ig-list-strip-cell">
                      <button
                        type="button"
                        className="ig-gen-preview-hit ig-gen-preview-hit--block"
                        onClick={() => openLightbox(src)}
                        title="查看大图"
                        aria-label="查看大图"
                      >
                        <img src={src} alt="" referrerPolicy="no-referrer" loading="lazy" />
                      </button>
                      {renderImageOverlay(src, rec)}
                    </div>
                  ))
                  ) : (
                    <div className="ig-list-image-wrap ig-preview-slot">
                      <div className="ig-gen-failed-slot ig-gen-failed-slot--fill">
                        <span className="ig-gen-failed-label">生成失败</span>
                        <p className="ig-gen-failed-msg">
                          {rec.generationError?.trim() || '无可用图片'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="ig-card-actions">
                  <button
                    type="button"
                    className="ig-list-toolbar-btn"
                    onClick={() => {
                      reloadGenRecordIntoDock(rec);
                      showToast(
                        rec.referenceInputs?.length
                          ? '已载入该次生成使用的提示词与参考图，可修改后再生成'
                          : '已载入提示词，可修改后重新生成',
                        'i',
                      );
                    }}
                  >
                    ✎ 重新编辑
                  </button>
                  <button
                    type="button"
                    className="ig-list-toolbar-btn"
                    onClick={() =>
                      regenerateFromRecord(rec)
                    }
                  >
                    ⟳ 重新生成
                  </button>
                  <div className="ig-list-more">
                    <button
                      type="button"
                      className="ig-list-toolbar-btn ig-list-toolbar-btn--icon"
                      aria-expanded={gridMoreOpenId === rec.id}
                      aria-haspopup="menu"
                      title="更多"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGridMoreOpenId((cur) => (cur === rec.id ? null : rec.id));
                      }}
                    >
                      <span aria-hidden>⋯</span>
                    </button>
                    {gridMoreOpenId === rec.id ? (
                      <div className="ig-grid-more-menu ig-gen-more-menu--below" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="ig-grid-more-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGridMoreOpenId(null);
                            setDeleteConfirmRec(rec);
                          }}
                        >
                          <svg
                            className="ig-grid-more-trash"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            aria-hidden
                          >
                            <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />
                          </svg>
                          删除
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
              );
              })}
              {hasMoreGenerations ? (
                <div
                  ref={loadMoreSentinelRef}
                  className="ig-feed-load-more"
                  role="status"
                  aria-live="polite"
                >
                  <span className="ig-feed-load-more-text">继续向下滚动加载更多…</span>
                </div>
              ) : null}
            </>
          )}
        </main>

        <div className="ig-bottom-area">
          <div
            className={`ig-bottom-dock${dockExpanded ? '' : ' ig-bottom-dock--compact'}`}
          >
            <div className="ig-dock-main">
              <div className="ig-ref-cluster">
                <input
                  ref={refInputRef}
                  type="file"
                  accept="image/*"
                  multiple={openAiGptImageEdit}
                  className="ig-hidden-file"
                  onChange={(e) => void onReferenceInputChange(e.target.files)}
                />
                {showRefStrip ? (
                  <div className="ig-ref-strip" aria-label="已选参考图">
                    {referenceImages.map((img) => (
                      <div key={img.id} className="ig-ref-strip-item">
                        <img src={img.thumb} alt="" className="ig-ref-strip-thumb" />
                        <button
                          type="button"
                          className="ig-ref-strip-remove"
                          onClick={() => removeReferenceImage(img.id)}
                          aria-label="移除此参考图"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {canShowRefAddBtn ? (
                  <button
                    type="button"
                    className={`ig-ref-btn${openAiGptImage && !openAiGptImageEdit ? ' ig-ref-btn--disabled' : ''}`}
                    onClick={() => {
                      if (openAiGptImage && !openAiGptImageEdit) {
                        showToast('GPT Image 1 不支持根据图片生图', 'i');
                        return;
                      }
                      refInputRef.current?.click();
                    }}
                    title={
                      openAiGptImageEdit
                        ? referenceImages.length
                          ? `继续添加参考图（至多 ${GPT_IMAGE_2_MAX_REFERENCE_IMAGES} 张）`
                          : '可多选多张参考图（图生图）'
                        : openAiGptImage
                          ? 'GPT Image 1 不支持根据图片生图'
                          : '上传参考图（单张）'
                    }
                  >
                    {showRefThumbInMainBtn && referenceImages[0] ? (
                      <img
                        src={referenceImages[0].thumb}
                        alt=""
                        className="ig-ref-thumb"
                      />
                    ) : (
                      <>
                        <span className="ig-plus">+</span>
                        <span>{openAiGptImageEdit ? '图片' : '图片'}</span>
                      </>
                    )}
                  </button>
                ) : null}
                {referenceImages.length ? (
                  <button
                    type="button"
                    className="ig-ref-clear"
                    onClick={clearReferenceImages}
                    title={
                      referenceImages.length > 1 ? '清空全部参考图' : '移除参考图'
                    }
                  >
                    ✕
                  </button>
                ) : null}
              </div>
              {mentionUiEnabled ? (
                <div
                  className={`ig-prompt-wrap${dockExpanded ? '' : ' ig-prompt-wrap--collapsed'}`}
                >
                  <div className="ig-prompt-highlight-layer" inert ref={promptDockHighlightRef}>
                    <PromptTextHighlight text={prompt} />
                  </div>
                  <textarea
                    ref={promptDockTextareaRef}
                    className={`ig-prompt ig-prompt--with-highlight${
                      dockExpanded ? '' : ' ig-prompt--collapsed'
                    }`}
                    placeholder="结合多张参考图描述画面，输入 @ 可提及图1、图2…（与左侧顺序一致）"
                    value={prompt}
                    onChange={handleDockPromptChange}
                    onSelect={(e) => {
                      promptDockCursorRef.current = e.currentTarget.selectionStart ?? 0;
                    }}
                    onKeyUp={(e) => {
                      promptDockCursorRef.current = e.currentTarget.selectionStart ?? 0;
                    }}
                    onClick={(e) => {
                      promptDockCursorRef.current = e.currentTarget.selectionStart ?? 0;
                    }}
                    rows={dockExpanded ? 3 : 1}
                    {...promptHighlightOverlayHandlers(() => promptDockHighlightRef.current)}
                    onBlur={() => {
                      window.setTimeout(() => {
                        setDockMentionState({ open: false, query: '' });
                      }, 120);
                    }}
                    onMouseMove={onDockPromptMouseMoveMentionThumb}
                    onMouseLeave={onDockPromptMouseLeaveMentionThumb}
                    onFocus={() => {
                      if (!dockExpanded) setDockExpanded(true);
                    }}
                  />
                </div>
              ) : (
                <textarea
                  className={`ig-prompt${dockExpanded ? '' : ' ig-prompt--collapsed'}`}
                  placeholder={
                    openAiGptImageEdit
                      ? '结合参考图（可多张）输入创意描述'
                      : '结合图片，输入创意描述'
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={dockExpanded ? 3 : 1}
                  onFocus={() => {
                    if (!dockExpanded) setDockExpanded(true);
                  }}
                />
              )}
              {!dockExpanded ? (
                <button
                  type="button"
                  className="ig-send"
                  onClick={onSubmit}
                  title={loading ? `生成中，点击继续排队` : '生成'}
                >
                  {loading && genQueuePlaceholders.length > 0
                    ? `+${genQueuePlaceholders.length}`
                    : '↑'}
                </button>
              ) : null}
            </div>
            {dockMentionState.open && filteredDockMentionCandidates.length > 0 ? (
              <div className="ig-dock-mention-list" role="listbox" aria-label="选择要提及的参考图">
                {filteredDockMentionCandidates.map((c) => (
                  <button
                    key={c.nodeId}
                    type="button"
                    role="option"
                    className="ig-dock-mention-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyDockPromptMention(c)}
                  >
                    <span>{c.mention}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{c.name}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {dockExpanded ? (
              <div className="ig-dock-toolbar">
                <button
                  type="button"
                  className="ig-dock-collapse"
                  onClick={collapseDock}
                  title="收起"
                  aria-label="收起输入区"
                >
                  <span className="ig-dock-collapse-ico" aria-hidden />
                </button>
                <div className="ig-dock-gen-toolbar" ref={genModeDockRef}>
                  <button
                    type="button"
                    className="ig-pill ig-pill--toolbar-gen"
                    aria-haspopup="menu"
                    aria-expanded={genModeMenuOpen}
                    aria-label={
                      !groupGenEnabled
                        ? '生成方式：单张（当前模型不支持组图）'
                        : genMode === 'single'
                          ? '生成方式：单张，点击可选组图'
                          : '生成方式：组图，点击可选单张'
                    }
                    onClick={() => setGenModeMenuOpen((open) => !open)}
                  >
                    <span>{genMode === 'single' ? '单张' : '组图'}</span>
                    <span className="ig-dock-gen-chevron-inline" aria-hidden>
                      ▾
                    </span>
                  </button>
                  {genModeMenuOpen ? (
                    <div className="ig-dock-gen-popmenu" role="menu" aria-label="选择生成方式">
                      <button
                        type="button"
                        role="menuitem"
                        className={`ig-dock-gen-menu-item${genMode === 'single' ? ' is-active' : ''}`}
                        onClick={() => {
                          setGenMode('single');
                          setGenModeMenuOpen(false);
                        }}
                      >
                        单张
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={`ig-dock-gen-menu-item${genMode === 'group' ? ' is-active' : ''}`}
                        disabled={!groupGenEnabled}
                        title={groupGenEnabled ? undefined : '组图暂仅支持 Gemini 2.5 Flash Image'}
                        onClick={() => {
                          if (!groupGenEnabled) return;
                          setGenMode('group');
                          setGenModeMenuOpen(false);
                        }}
                      >
                        组图
                      </button>
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="ig-pill"
                  data-ig-params-anchor
                  onClick={() => setParamsOpen((open) => !open)}
                >
                  {openAiGptImage ? openAiImageSize : resolutionUi}
                </button>
                {openAiGptImage ? null : (
                  <button
                    type="button"
                    className="ig-pill"
                    data-ig-params-anchor
                    onClick={() => setParamsOpen((open) => !open)}
                  >
                    {aspectUi}
                  </button>
                )}
                {groupGenEnabled && genMode === 'group' ? (
                  <div className="ig-count-wrap" ref={groupCountPopRef}>
                    <button
                      type="button"
                      className="ig-pill"
                      aria-haspopup="dialog"
                      aria-expanded={groupCountPopOpen}
                      aria-label={`生成张数：${groupCount} 张`}
                      onClick={() => setGroupCountPopOpen((open) => !open)}
                    >
                      {groupCount}张
                    </button>
                    {groupCountPopOpen ? (
                      <div className="ig-count-pop" role="dialog" aria-label="选择生成张数">
                        <div className="ig-count-pop-title">生成张数</div>
                        <div className="ig-count-pop-row">
                          <input
                            type="range"
                            className="ig-count-range"
                            min={2}
                            max={4}
                            step={1}
                            value={groupCount}
                            onChange={(e) => setGroupCount(Number(e.target.value))}
                            aria-valuemin={2}
                            aria-valuemax={4}
                            aria-valuenow={groupCount}
                            aria-label="滑动选择张数"
                          />
                          <span className="ig-count-num" aria-hidden>
                            {groupCount}
                          </span>
                        </div>
                        <span className="ig-dock-count-cap">上限 4 张</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="ig-dock-spacer" />
                <button
                  type="button"
                  className="ig-send"
                  onClick={onSubmit}
                  title={loading ? `生成中，点击继续排队` : '生成'}
                >
                  {loading && genQueuePlaceholders.length > 0
                    ? `+${genQueuePlaceholders.length}`
                    : '↑'}
                </button>
              </div>
            ) : null}
            {dockExpanded && mentionUiEnabled ? (
              <p className="ig-dock-mention-hint">
                <code>@</code> 提及参考图（悬停可预览；提交时自动替换为图N）
              </p>
            ) : null}
            <p className="ig-dock-tip">
              {IMAGE_GEN_DOCK_DISCLAIMER}
            </p>
          </div>
        </div>

        <ImageGenMentionThumbPopover hover={dockMentionThumbHover} />

        {loading ? <div className="ig-loading-bar" /> : null}

        <ImageLightbox
          imageSrc={lightboxSrc}
          onClose={closeLightbox}
          index={lightboxIndex ?? undefined}
          total={lightboxTotal}
          canPrev={canLightboxPrev}
          canNext={canLightboxNext}
          onPrev={() =>
            canLightboxPrev && setLightboxIndex((i) => (i != null && i > 0 ? i - 1 : i))
          }
          onNext={() =>
            canLightboxNext &&
            setLightboxIndex((i) => {
              if (i == null) return i;
              return i < lightboxGallery.length - 1 ? i + 1 : i;
            })
          }
          toolbarActions={
            lightboxActiveItem
              ? [
                  {
                    key: 'download',
                    label: '下载',
                    icon: '⤓',
                    title: '下载',
                    onClick: () => {
                      const { src, rec } = lightboxActiveItem;
                      const fname =
                        (rec.prompt.trim().slice(0, 16).replace(/\s+/g, '_') || `gen_${rec.id}`) +
                        '.png';
                      triggerDownload(src, fname);
                    },
                  },
                  {
                    key: 'library',
                    label: '加入素材库',
                    icon: '📦',
                    title: '加入素材库',
                    onClick: () =>
                      openAddToLibrary(lightboxActiveItem.src, lightboxActiveItem.rec),
                  },
                ]
              : undefined
          }
        />

        <ConfirmModal
          open={deleteConfirmRec != null}
          title="删除这条生成记录？"
          confirmVariant="danger"
          confirmLabel="删除"
          onCancel={() => setDeleteConfirmRec(null)}
          onConfirm={() => {
            if (!deleteConfirmRec) return;
            const id = deleteConfirmRec.id;
            setGenerations((g) => g.filter((x) => x.id !== id));
            setDeleteConfirmRec(null);
            setGridMoreOpenId(null);
            setGridPreviewIndex((m) => {
              const next = { ...m };
              delete next[id];
              return next;
            });
            if (libModalRec?.id === id) closeAddToLibrary();
            showToast('已删除', 'i');
          }}
        >
          <p>删除后无法恢复，本地缓存会同步移除该条记录。</p>
        </ConfirmModal>

        {libModalSrc ? (
          <div className="ig-modal-mask" onClick={closeAddToLibrary}>
            <div
              className="ig-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="ig-modal-head">
                <span>加入素材库</span>
                <button
                  type="button"
                  className="ig-modal-close"
                  onClick={closeAddToLibrary}
                  disabled={addingToLib}
                >
                  ✕
                </button>
              </div>
              <div className="ig-modal-body">
                <div className="ig-modal-preview">
                  <img src={libModalSrc} alt="" referrerPolicy="no-referrer" />
                </div>
                <div className="ig-modal-fields">
                  <label className="ig-modal-label" htmlFor="ig-lib-asset-name">
                    素材名称
                  </label>
                  <input
                    id="ig-lib-asset-name"
                    type="text"
                    className="ig-modal-input"
                    value={libAssetName}
                    onChange={(e) => setLibAssetName(e.target.value)}
                    placeholder="为此素材命名，留空则用提示词摘要"
                    disabled={addingToLib}
                    maxLength={200}
                    autoComplete="off"
                  />
                  <span className="ig-modal-label">素材组</span>
                  {libGroupsLoading ? (
                    <div className="ig-modal-tip">正在加载素材组…</div>
                  ) : !hasLibraryAuth() ? (
                    <div className="ig-modal-tip">
                      请先在「素材库」页配置系统访问令牌和用户 ID。
                    </div>
                  ) : libGroupOptions.length === 0 ? (
                    <div className="ig-modal-tip">暂无可用素材组，请稍后重试。</div>
                  ) : (
                    <div className="ig-modal-group-list" role="radiogroup" aria-label="虚拟人像素材组">
                      {libGroupOptions.map((opt) => (
                        <label
                          key={opt.key}
                          className={`ig-modal-group-item${
                            selectedLibGroupId === opt.groupId ? ' selected' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="ig-lib-group"
                            checked={selectedLibGroupId === opt.groupId}
                            onChange={() => setSelectedLibGroupId(opt.groupId)}
                            disabled={addingToLib}
                          />
                          <span className="ig-modal-group-name">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="ig-modal-actions">
                <button
                  type="button"
                  className="ig-btn"
                  onClick={closeAddToLibrary}
                  disabled={addingToLib}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="ig-btn ig-btn-primary"
                  onClick={() => void submitAddToLibrary()}
                  disabled={!selectedLibGroupId.trim() || addingToLib || libGroupsLoading}
                >
                  {addingToLib ? '加入中…' : '加入'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ImageGenPage;
